


import { useEffect, useRef, useState } from "react";
import "./App.css";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import AgbPage from "./pages/AgbPage";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import { auth, db, functions } from "./firebase";
import { doc, updateDoc, onSnapshot } from "firebase/firestore";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./i18n/LanguageProvider";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import EmailChangeConfirmPage from "./pages/EmailChangeConfirmPage";
import ProfileCompletionPage from "./pages/ProfileCompletionPage";
import AppleAccountOnboardingPage from "./pages/AppleAccountOnboardingPage";
import ContactPage from "./pages/ContactPage";
import FaqPage from "./pages/FaqPage";
import CompetitionTermsPage from "./pages/CompetitionTermsPage";
import { registerNativePushNotifications } from "./services/nativePushNotifications";
import {
  authenticateWithBiometrics,
  consumeBiometricOfferAfterLogin,
  getBiometricAvailability,
  isBiometricLoginEnabledForUser,
  setBiometricLoginEnabledForUser,
} from "./services/biometricAuth";
import { AUTH_STATES, resolveAuthState } from "./authState";
import {
  getAuthProviderIds,
  requiresEmailVerificationForUser,
} from "./utils/authProviders";
import {
  clearAccountDeletionInProgress,
  isAccountDeletionInProgress,
} from "./services/providerAwareAccountDeletion";

const LOGOUT_IN_PROGRESS_KEY = "vaultquest:logoutInProgress";
const NATIVE_SPLASH_DURATION_MS = 1450;
const NATIVE_SPLASH_EXIT_MS = 370;
const syncMyAccountEmailSecure = httpsCallable(functions, "syncMyAccountEmailSecure");

function isNativeVaultQuestApp() {
  return Capacitor.isNativePlatform();
}

function isLogoutInProgress() {
  if (typeof window === "undefined") return false;

  try {
    return window.sessionStorage?.getItem(LOGOUT_IN_PROGRESS_KEY) === "1";
  } catch {
    return false;
  }
}

function clearLogoutInProgress() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.removeItem(LOGOUT_IN_PROGRESS_KEY);
  } catch {
    // Session storage can be unavailable in private or restricted browser contexts.
  }
}

function resetDocumentScroll() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.documentElement.scrollLeft = 0;
  document.body.scrollTop = 0;
  document.body.scrollLeft = 0;
  document.getElementById("root")?.scrollTo?.({ left: 0, top: 0, behavior: "auto" });
}

function isKeyboardEditableElement(element) {
  return (
    element &&
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) &&
    !element.readOnly &&
    !element.disabled
  );
}

function hasFocusedKeyboardEditable() {
  if (typeof document === "undefined") return false;
  return isKeyboardEditableElement(document.activeElement);
}

function isTabletViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(min-width: 721px) and (max-width: 1180px)")?.matches === true;
}

function resetHorizontalDocumentScroll() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  if (window.scrollX !== 0) {
    window.scrollTo({ top: window.scrollY, left: 0, behavior: "auto" });
  }
  document.documentElement.scrollLeft = 0;
  document.body.scrollLeft = 0;
  document.getElementById("root")?.scrollTo?.({ left: 0, behavior: "auto" });
}

function RouteScrollReset() {
  const location = useLocation();

  useEffect(() => {
    resetDocumentScroll();
    const frame = window.requestAnimationFrame(resetDocumentScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search]);

  return null;
}

function ProfileRequiredRoutes({ firebaseUser }) {
  const isNativeAppleUser =
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === "ios" &&
    getAuthProviderIds(firebaseUser).includes("apple.com");

  return (
    <Routes>
      <Route path="/agb" element={<AgbPage showBackButton={true} />} />
      <Route path="/datenschutz" element={<DatenschutzPage showBackButton={true} />} />
      <Route
        path="*"
        element={
          isNativeAppleUser
            ? <AppleAccountOnboardingPage firebaseUser={firebaseUser} />
            : <ProfileCompletionPage />
        }
      />
    </Routes>
  );
}

function NativeSplashScreen({ exiting, onExited }) {
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (!exiting) return undefined;
    setIsLeaving(true);
    const exitTimeout = window.setTimeout(onExited, NATIVE_SPLASH_EXIT_MS);
    return () => window.clearTimeout(exitTimeout);
  }, [exiting, onExited]);

  return (
    <div
      className={`vq-native-splash${isLeaving ? " vq-native-splash--leaving" : ""}`}
      aria-label="VaultQuest"
    >
      <div className="vq-native-splash__mark-wrap">
        <span className="vq-native-splash__aura" aria-hidden="true" />
        <img
          src="/vaultquest-logo.png"
          alt=""
          className="vq-native-splash__mark"
          draggable="false"
        />
      </div>
      <div className="vq-native-splash__copy">
        <div className="vq-native-splash__claim">
          Enter the <span>Vault</span>. Start your <span>Quest</span>.
        </div>
      </div>
    </div>
  );
}

function AuthStatusScreen({ hasError = false }) {
  const { t } = useLanguage();
  return (
    <div className="vq-auth-status-screen" aria-live="polite">
      <img src="/vaultquest-logo.png" alt="VaultQuest" />
      <div>{hasError ? t("authState.loadError") : t("authState.loading")}</div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(undefined);
  const [authState, setAuthState] = useState(AUTH_STATES.AUTH_LOADING);
  const [loading, setLoading] = useState(true);
  const [startupError, setStartupError] = useState("");
  const [biometricVerifiedUid, setBiometricVerifiedUid] = useState(null);
  const [biometricOfferUid, setBiometricOfferUid] = useState(null);
  const [biometricOfferLoading, setBiometricOfferLoading] = useState(false);
  const [nativeSplashVisible, setNativeSplashVisible] = useState(() => isNativeVaultQuestApp());
  const [nativeSplashMinDurationDone, setNativeSplashMinDurationDone] = useState(() => !isNativeVaultQuestApp());
  const profileUnsubscribeRef = useRef(null);
  const biometricUnlockingRef = useRef(false);
  const accountEmailSyncInFlightRef = useRef(false);

  useEffect(() => {
    if (!isNativeVaultQuestApp()) return undefined;
    const timeout = window.setTimeout(() => setNativeSplashMinDurationDone(true), NATIVE_SPLASH_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const scheduleHorizontalReset = ({ allowWhileInputFocused = false } = {}) => {
      if (!allowWhileInputFocused && hasFocusedKeyboardEditable()) return;
      resetHorizontalDocumentScroll();
      window.setTimeout(() => {
        if (!allowWhileInputFocused && hasFocusedKeyboardEditable()) return;
        resetHorizontalDocumentScroll();
      }, 80);
      window.setTimeout(() => {
        if (!allowWhileInputFocused && hasFocusedKeyboardEditable()) return;
        resetHorizontalDocumentScroll();
      }, 260);
    };
    const scheduleAfterInputBlur = () => {
      window.setTimeout(() => scheduleHorizontalReset({ allowWhileInputFocused: false }), 120);
      window.setTimeout(() => scheduleHorizontalReset({ allowWhileInputFocused: false }), 320);
    };

    window.addEventListener("focusout", scheduleAfterInputBlur);
    window.addEventListener("resize", scheduleHorizontalReset);
    window.addEventListener("orientationchange", scheduleHorizontalReset);
    const shouldObserveVisualViewportScroll = !isTabletViewport();

    window.visualViewport?.addEventListener("resize", scheduleHorizontalReset);
    if (shouldObserveVisualViewportScroll) {
      window.visualViewport?.addEventListener("scroll", scheduleHorizontalReset);
    }

    return () => {
      window.removeEventListener("focusout", scheduleAfterInputBlur);
      window.removeEventListener("resize", scheduleHorizontalReset);
      window.removeEventListener("orientationchange", scheduleHorizontalReset);
      window.visualViewport?.removeEventListener("resize", scheduleHorizontalReset);
      if (shouldObserveVisualViewportScroll) {
        window.visualViewport?.removeEventListener("scroll", scheduleHorizontalReset);
      }
    };
  }, []);

useEffect(() => {
  const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
    if (typeof profileUnsubscribeRef.current === "function") {
      profileUnsubscribeRef.current();
      profileUnsubscribeRef.current = null;
    }

    setStartupError("");

    if (!currentUser) {
      clearLogoutInProgress();
      clearAccountDeletionInProgress();
      setFirebaseUser(null);
      setAuthState(resolveAuthState(null, undefined));
      setUser(null);
      setBiometricVerifiedUid(null);
      setBiometricOfferUid(null);
      setLoading(false);
      return;
    }

    setFirebaseUser(currentUser);
    setAuthState(AUTH_STATES.AUTH_LOADING);

    const userRef = doc(db, "users", currentUser.uid);

    profileUnsubscribeRef.current = onSnapshot(
      userRef,
      async (docSnap) => {
        if (auth.currentUser?.uid !== currentUser.uid) return;

          if (docSnap.exists()) {
            const userData = docSnap.data() || {};
            const authEmail = String(currentUser.email || "").trim().toLowerCase();
            const storedEmail = String(userData.email || "").trim().toLowerCase();
            const authEmailVerified = currentUser.emailVerified === true;

            if (
              authEmail &&
              !accountEmailSyncInFlightRef.current &&
              (storedEmail !== authEmail || userData.emailVerified !== authEmailVerified)
            ) {
              accountEmailSyncInFlightRef.current = true;
              syncMyAccountEmailSecure({})
                .catch((error) => {
                  console.warn("Account email sync skipped:", error?.code || error?.message || "unknown");
                })
                .finally(() => {
                  accountEmailSyncInFlightRef.current = false;
                });
            }

          // 🔹 timezone setzen falls fehlt
          if (!userData.timeZone) {
            try {
              await updateDoc(userRef, {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
              });
            } catch (error) {
              console.error("Failed to ensure user timeZone:", error?.code || error?.message || "unknown");
            }
          }

          // 👉 DAS ist der wichtige Teil
          setUser({
            uid: currentUser.uid,
            ...userData,
            email: currentUser.email || userData.email || "",
            emailVerified: authEmailVerified,
            providerIds: getAuthProviderIds(currentUser),
            requiresEmailVerification: requiresEmailVerificationForUser(
              currentUser,
              userData
            ),
          });
          setAuthState(resolveAuthState(currentUser, true));
        } else {
          if (isAccountDeletionInProgress(currentUser.uid)) {
            setFirebaseUser(null);
            setUser(null);
            setBiometricVerifiedUid(null);
            setBiometricOfferUid(null);
            setAuthState(resolveAuthState(null, undefined));
            setLoading(false);
            return;
          }

          setUser(null);
          setAuthState(resolveAuthState(currentUser, false));
        }

        setLoading(false);
      },
      (error) => {
        if (
          auth.currentUser?.uid !== currentUser.uid ||
          isLogoutInProgress() ||
          isAccountDeletionInProgress(currentUser.uid)
        ) {
          if (isAccountDeletionInProgress(currentUser.uid)) {
            setFirebaseUser(null);
            setAuthState(resolveAuthState(null, undefined));
          }
          setUser(null);
          setLoading(false);
          return;
        }

        console.error("Failed to load signed-in user profile:", error?.code || error?.message || "unknown");
        setStartupError("Dein Profil konnte nicht geladen werden. Bitte pruefe deine Verbindung und starte die App erneut.");
        setUser(null);
        setAuthState(AUTH_STATES.AUTH_LOADING);
        setLoading(false);
      }
    );
  }, (error) => {
    console.error("Failed to initialize authentication:", error?.code || error?.message || "unknown");
    setStartupError("Die Anmeldung konnte nicht initialisiert werden. Bitte starte die App erneut.");
    setFirebaseUser(undefined);
    setAuthState(AUTH_STATES.AUTH_LOADING);
    setUser(null);
    setLoading(false);
  });

  return () => {
    if (typeof profileUnsubscribeRef.current === "function") {
      profileUnsubscribeRef.current();
      profileUnsubscribeRef.current = null;
    }
    unsubscribeAuth();
  };
}, []);

useEffect(() => {
  if (!user?.uid) return;
  if (user.requiresEmailVerification === true && user.emailVerified !== true) return;

  registerNativePushNotifications().catch((error) => {
    console.warn("Native push registration skipped:", error?.code || error?.message || "unknown");
  });
}, [user?.uid, user?.requiresEmailVerification, user?.emailVerified]);

useEffect(() => {
  const uid = user?.uid;
  if (!uid) return;
  if (user.requiresEmailVerification === true && user.emailVerified !== true) return;

  if (biometricVerifiedUid && biometricVerifiedUid !== uid) {
    setBiometricVerifiedUid(null);
  }

  if (!isBiometricLoginEnabledForUser(uid)) {
    setBiometricVerifiedUid(uid);
    return;
  }

  if (biometricVerifiedUid === uid || biometricUnlockingRef.current) return;

  let cancelled = false;

  const unlock = async () => {
    biometricUnlockingRef.current = true;
    const verified = await authenticateWithBiometrics();

    if (cancelled) {
      biometricUnlockingRef.current = false;
      return;
    }

    if (verified) {
      setBiometricVerifiedUid(uid);
      biometricUnlockingRef.current = false;
      return;
    }

    setBiometricVerifiedUid(null);
    setBiometricOfferUid(null);
    biometricUnlockingRef.current = false;
    await signOut(auth);
  };

  unlock();

  return () => {
    cancelled = true;
  };
}, [user?.uid, user?.requiresEmailVerification, user?.emailVerified, biometricVerifiedUid]);

useEffect(() => {
  const uid = user?.uid;
  if (!uid) return;
  if (user.requiresEmailVerification === true && user.emailVerified !== true) return;
  if (!consumeBiometricOfferAfterLogin(uid)) return;
  if (isBiometricLoginEnabledForUser(uid)) return;

  let cancelled = false;

  const prepareOffer = async () => {
    const availability = await getBiometricAvailability();
    if (!cancelled && availability?.isAvailable) {
      setBiometricOfferUid(uid);
    }
  };

  prepareOffer();

  return () => {
    cancelled = true;
  };
}, [user?.uid, user?.requiresEmailVerification, user?.emailVerified]);

const handleEnableBiometricLogin = async () => {
  const uid = user?.uid;
  if (!uid) return;

  setBiometricOfferLoading(true);
  const verified = await authenticateWithBiometrics();
  setBiometricOfferLoading(false);

  if (!verified) return;

  setBiometricLoginEnabledForUser(uid, true);
  setBiometricVerifiedUid(uid);
  setBiometricOfferUid(null);
};

const biometricLockRequired =
  user?.uid &&
  isBiometricLoginEnabledForUser(user.uid) &&
  !(user.requiresEmailVerification === true && user.emailVerified !== true) &&
  biometricVerifiedUid !== user.uid;

const emailVerificationRequired =
  user?.requiresEmailVerification === true &&
  user?.emailVerified !== true;

const nativeSplashExiting =
  nativeSplashVisible && nativeSplashMinDurationDone && !loading;

const profileRequired =
  authState === AUTH_STATES.PROFILE_REQUIRED && Boolean(firebaseUser?.uid);

return (
  <LanguageProvider>
    {nativeSplashVisible && (
      <NativeSplashScreen
        exiting={nativeSplashExiting}
        onExited={() => setNativeSplashVisible(false)}
      />
    )}
    {!loading && (
      <Router>
      <RouteScrollReset />
      {startupError && (
        <div
          style={{
            position: "fixed",
            top: "calc(16px + var(--vq-safe-area-top))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            width: "calc(100% - 32px)",
            maxWidth: 420,
            padding: "14px 16px",
            borderRadius: 14,
            border: "2px solid rgba(255,255,255,0.35)",
            background: "linear-gradient(135deg, #ff007f 0%, #7a2bff 100%)",
            color: "white",
            fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
            fontWeight: 900,
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          {startupError}
        </div>
      )}
      {authState === AUTH_STATES.AUTH_LOADING ? (
        <AuthStatusScreen hasError={Boolean(startupError)} />
      ) : biometricLockRequired ? (
        <BiometricUnlockScreen />
      ) : profileRequired ? (
        <ProfileRequiredRoutes firebaseUser={firebaseUser} />
      ) : emailVerificationRequired ? (
        <EmailVerificationPage
          onVerified={() => {
            setUser((previousUser) =>
              previousUser
                ? { ...previousUser, emailVerified: true }
                : previousUser
            );
          }}
        />
      ) : (
      <>
      {biometricOfferUid === user?.uid && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "calc(18px + var(--vq-safe-area-top)) calc(18px + var(--vq-safe-area-right)) calc(18px + var(--vq-safe-area-bottom)) calc(18px + var(--vq-safe-area-left))",
            background: "rgba(0,0,0,0.72)",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 390,
              borderRadius: 22,
              padding: "24px 20px 20px",
              background: "linear-gradient(135deg, rgba(255,0,170,0.96), rgba(122,43,255,0.96))",
              border: "2px solid rgba(255,255,255,0.30)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.44)",
              color: "white",
              fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 1000,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Face ID aktivieren?
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                fontWeight: 800,
                color: "rgba(255,255,255,0.86)",
                marginBottom: 20,
              }}
            >
              Face ID für zukünftige Anmeldungen aktivieren? Es werden keine Passwörter lokal gespeichert.
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <button
                type="button"
                onClick={handleEnableBiometricLogin}
                disabled={biometricOfferLoading}
                style={{
                  height: 50,
                  borderRadius: 16,
                  border: "2px solid rgba(255,255,255,0.50)",
                  background: "#ffffff",
                  color: "#120528",
                  fontWeight: 1000,
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  cursor: biometricOfferLoading ? "wait" : "pointer",
                }}
              >
                {biometricOfferLoading ? "Prüfe..." : "Jetzt aktivieren"}
              </button>
              <button
                type="button"
                onClick={() => setBiometricOfferUid(null)}
                disabled={biometricOfferLoading}
                style={{
                  height: 46,
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.08)",
                  color: "white",
                  fontWeight: 1000,
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  cursor: biometricOfferLoading ? "wait" : "pointer",
                }}
              >
                Später
              </button>
            </div>
          </div>
        </div>
      )}
      <Routes>

        <Route
          path="/"
          element={authState === AUTH_STATES.READY ? <DashboardPage /> : <LoginPage />}
        />

        <Route path="/landing" element={<LandingPage />} />

        <Route
          path="/login"
          element={authState === AUTH_STATES.READY ? <Navigate to="/" /> : <LoginPage />}
        />

        <Route
  path="/reset-password"
  element={<ResetPasswordPage />}
/>

        <Route
          path="/email-change-confirm"
          element={<EmailChangeConfirmPage />}
        />

   

        <Route
          path="/app"
          element={<Navigate to="/" replace />}
        />

        <Route path="/impressum" element={<ImpressumPage showBackButton={true} />} />
        <Route path="/agb" element={<AgbPage showBackButton={true} />} />
        <Route path="/datenschutz" element={<DatenschutzPage showBackButton={true} />} />
        <Route path="/wettbewerbsbedingungen" element={<CompetitionTermsPage showBackButton={true} />} />
        <Route path="/contact" element={<ContactPage showBackButton={true} />} />
        <Route path="/faqs" element={<FaqPage showBackButton={true} />} />
      </Routes>
      </>
      )}
    </Router>
    )}
  </LanguageProvider>
);
}

function BiometricUnlockScreen() {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
        color: "#fff",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        padding: "calc(24px + var(--vq-safe-area-top)) calc(20px + var(--vq-safe-area-right)) calc(24px + var(--vq-safe-area-bottom)) calc(20px + var(--vq-safe-area-left))",
        boxSizing: "border-box",
        textAlign: "center",
      }}
    >
      <div>
        <img
          src="/vaultquest-logo.png"
          alt="VaultQuest"
          style={{
            width: 118,
            height: 118,
            objectFit: "contain",
            marginBottom: 18,
            filter: "drop-shadow(0 18px 34px rgba(168,85,247,0.35))",
          }}
        />
        <div
          style={{
            fontSize: 22,
            fontWeight: 1000,
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          VaultQuest entsperren
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.68)",
            fontSize: 14,
            fontWeight: 800,
            lineHeight: 1.5,
          }}
        >
          Face ID oder Touch ID wird gestartet.
        </div>
      </div>
    </div>
  );
}

export default App;
